import { useEffect, useRef, useState } from 'react';
import { useComponentConfig } from '@namespace/cms';
import { useSearchParams } from '@namespace/router';
import { NEXT_MORNING_FILTER, TODAY_FILTER } from '../../../../constants';
import { useChangeDefaultFilter } from '../../../../hooks/useChangeDefaultFilter';
import { useFetchEvents } from '../../../../hooks/useFetchEvents';
import { initialState, useEventsByIdsToShow } from '../../../../store';
import Line from '../../../Line';

const emptyArr = [];
export const CommonSportLine = ({
  filter,
  defaultFilter,
  sport,
  categoryIds = emptyArr,
  tournamentsIds = emptyArr,
  lineKey,
  serviceId,
  activeHeadGroups,
  isShowFlag,
  isExpanded,
  isShowEmpty,
  isCollapsibleTournaments,
  isLiveEvents,
  onDefaultFilterChange,
  isReady
}) => {
  const {
    isHeadGroupsSortingEnabled,
    isShowEventGroup,
    activeHeadGroupsAmount,
    initialEventsLimit,
    moreEventsLimit
  } = useComponentConfig();
  const isChangeDefaultFilter = useChangeDefaultFilter();
  const onDefaultFilterChangeRef = useRef(onDefaultFilterChange);
  useEffect(() => {
    onDefaultFilterChangeRef.current = onDefaultFilterChange;
  }, [onDefaultFilterChange]);
  const filterRef = useRef(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  const { timeFilter } = useSearchParams();
  const timeFilterRef = useRef(timeFilter);
  useEffect(() => {
    timeFilterRef.current = timeFilter;
  }, [timeFilter]);

  const eventsToShow = useEventsByIdsToShow(lineKey, tournamentsIds);

  const { isEventsLoading, loadMoreEvents, isLoadingMore } = useFetchEvents({
    lineKey,
    serviceId,
    filter,
    sportId: sport?.sportId,
    categoryIds,
    tournamentsIds,
    initialEventsLimit: initialEventsLimit || initialState.limit || 0,
    moreEventsLimit: moreEventsLimit || initialState.limit
  });
  const [isActualLoading, setIsActualLoading] = useState(isEventsLoading);

  // Note: do NOT depend on the current filter or onDefaultFilterChange - we need actual data but should not trigger updates on these changes because it breaks the order of updates (filter is being changed but loading is not yet started - results in incorrectly setting isActualLoading).
  useEffect(() => {
    const { days = '' } = filterRef.current || {};
    const isActiveFilterToday =
      new Date(days).toLocaleDateString() === new Date().toLocaleDateString() ||
      Boolean(filterRef.current?.[NEXT_MORNING_FILTER]);
    const isChangeDefaultFilterEligible =
      isChangeDefaultFilter &&
      isActiveFilterToday &&
      !timeFilterRef.current && // if present - the filter was set by the user
      (defaultFilter === TODAY_FILTER || defaultFilter === NEXT_MORNING_FILTER);

    if (isEventsLoading) {
      return;
    }

    // if we're not allowed to change filter at all - set loading as finished regardless of the fetch result
    if (!isChangeDefaultFilterEligible) {
      setIsActualLoading(false);
      return;
    }

    // if we're allowed to change filter and got no events - change the default filter
    if (eventsToShow.length === 0) {
      onDefaultFilterChangeRef.current();
      return;
    }

    // if we fetched events and got positive - set loading as finished
    setIsActualLoading(false);
  }, [
    defaultFilter,
    eventsToShow.length,
    isChangeDefaultFilter,
    isEventsLoading
  ]);

  return (
    <Line
      eventsToShow={eventsToShow}
      tournamentIds={tournamentsIds}
      isExpanded={isExpanded}
      isShowEventGroup={isShowEventGroup}
      isShowFlag={isShowFlag}
      isHeadGroupsSortingEnabled={isHeadGroupsSortingEnabled}
      isEventsLoading={isActualLoading}
      activeHeadGroupsAmount={activeHeadGroupsAmount}
      activeHeadGroups={activeHeadGroups}
      fetchEvents={loadMoreEvents}
      isLoadingMore={isLoadingMore}
      isShowEmpty={isShowEmpty}
      isCollapsibleTournaments={isCollapsibleTournaments}
      lineKey={lineKey}
      isLiveEvents={isLiveEvents}
      isReady={isReady}
    />
  );
};
